import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import PersonSelect from '../../../components/PersonSelect';
import { API_ORIGIN } from '../../../services/apiBase';

const STATUS_COLOR = {
  DRAFT:       { bg: '#e0e7ff', color: '#3730a3' },
  IN_PROGRESS: { bg: '#fef9c3', color: '#92400e' },
  LOCKED:      { bg: '#dcfce7', color: '#166534' },
};

export default function OasisList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roles = user?.roles || [];
  const canCreate = roles.some(r => ['ADMIN', 'DOCTOR', 'NURSE'].includes(r));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ patient_id: '', assessment_type: 'SOC', episode_id: '' });

  const apiBase = `${API_ORIGIN}/api/v1`;
  const headers = () => ({ 'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                           'Content-Type': 'application/json' });

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/oasis-assessments?per_page=100`, { headers: headers() });
      const d = await r.json();
      setItems(d.data?.data || d.data?.items || []);
    } catch { } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault();
    setCreating(true); setError('');
    try {
      const payload = { patient_id: form.patient_id, assessment_type: form.assessment_type };
      if (form.episode_id) payload.episode_id = form.episode_id;
      const r = await fetch(`${apiBase}/oasis-assessments`, {
        method: 'POST', headers: headers(), body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      const result = d.data || d;
      navigate(`/oasis/${result.assessment_id}`);
    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Failed to create');
    } finally { setCreating(false); }
  }

  return (
    <div style={{ padding: '24px 28px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2b1b22', margin: 0 }}>
          OASIS-E Assessments
        </h1>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} style={{
            padding: '9px 18px', background: 'linear-gradient(180deg,#94335a,#7b1e3c)',
            color: '#fff', border: 'none', borderRadius: 8, fontSize: 13,
            fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          }}>+ New Assessment</button>
        )}
      </div>

      {showCreate && (
        <div style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12,
          padding: 24, marginBottom: 20, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 16px' }}>Create Assessment</h2>
          {error && <div style={{ background: '#fdecea', color: '#b71c1c', padding: '8px 12px',
            borderRadius: 8, marginBottom: 12, fontSize: 12.5 }}>{error}</div>}
          <form onSubmit={create} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5e4a53', textTransform: 'uppercase',
                letterSpacing: '0.5px', marginBottom: 4 }}>Patient *</div>
              <PersonSelect role="patient" value={form.patient_id} required
                onChange={id => setForm(f => ({ ...f, patient_id: id }))}
                style={{ width: 280 }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5e4a53', textTransform: 'uppercase',
                letterSpacing: '0.5px', marginBottom: 4 }}>Assessment Type *</div>
              <select value={form.assessment_type} onChange={e => setForm(f => ({ ...f, assessment_type: e.target.value }))}
                style={{ padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
                {['SOC','ROC','FU','TRANSFER','DISCHARGE'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <button type="submit" disabled={creating} style={{
              padding: '9px 18px', background: creating ? '#90a4ae' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
              color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
            }}>{creating ? 'Creating…' : 'Create'}</button>
            <button type="button" onClick={() => setShowCreate(false)} style={{
              padding: '9px 18px', background: '#f3f4f6', color: '#374151',
              border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer',
            }}>Cancel</button>
          </form>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8b7681' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8b7681', background: '#fff',
          borderRadius: 12, border: '1px solid #ece3e7' }}>No OASIS assessments found.</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ece3e7', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9f5f6', borderBottom: '1px solid #ece3e7' }}>
                {['Type','Patient','Episode','HIPPS','Status','Created'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700,
                    fontSize: 11, color: '#5e4a53', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((a, i) => (
                <tr key={a.assessment_id} style={{ borderBottom: '1px solid #f3f4f6',
                  background: i % 2 === 0 ? '#fff' : '#fdf9fb' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: '#7b1e3c' }}>
                    {a.assessment_type}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 13 }}>
                    {a.patient_name || a.patient_id?.slice(0, 8) || '—'}
                  </td>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 11 }}>
                    {a.episode_id ? a.episode_id.slice(0, 8) + '…' : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {a.hipps_code ? (
                      <span style={{ background: '#7b1e3c', color: '#fff', padding: '2px 8px',
                        borderRadius: 4, fontWeight: 700, fontFamily: 'monospace' }}>{a.hipps_code}</span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      ...(STATUS_COLOR[a.status] || { bg: '#f3f4f6', color: '#374151' }),
                      background: (STATUS_COLOR[a.status] || {}).bg,
                    }}>{a.status}</span>
                  </td>
                  <td style={{ padding: '10px 14px', color: '#8b7681', fontSize: 12 }}>
                    {new Date(a.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <button onClick={() => navigate(`/oasis/${a.assessment_id}`)} style={{
                      padding: '5px 12px', background: '#f9f5f6', color: '#7b1e3c',
                      border: '1px solid #ece3e7', borderRadius: 6, fontSize: 12,
                      cursor: 'pointer', fontWeight: 600, fontFamily: 'Inter, sans-serif',
                    }}>{a.status === 'LOCKED' ? 'View' : 'Edit'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
